import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";

const PRIVACY_CONTACT = "admin@freelancertamal.com";
const LAST_UPDATED = "June 21, 2026";

export const Route = createFileRoute("/privacy")({
  head: () =>
    buildSeo({
      title: "Privacy Policy — Freelancer Tamal",
      description:
        "How Freelancer Tamal collects, uses, retains, and protects personal data submitted through this website.",
      path: "/privacy",
      ogEyebrow: "Privacy",
    }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Privacy", path: "/privacy" }]} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE.url}/privacy#webpage`,
          url: `${SITE.url}/privacy`,
          name: "Privacy Policy — Freelancer Tamal",
          description:
            "Privacy policy for the Freelancer Tamal website covering data collection, subprocessors, retention, and user rights.",
          inLanguage: "en",
          isPartOf: { "@id": `${SITE.url}/#website` },
          dateModified: "2026-06-21",
        }}
      />
      <Section className="pt-10 pb-24">
        <header className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Privacy</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">Privacy Policy</h1>
          <p className="mt-4 text-muted-foreground">
            This page is maintained by {SITE.fullName} to explain how this website handles
            personal data. It is not a legal contract or an independent certification. Last
            updated {LAST_UPDATED}.
          </p>
        </header>

        <article className="max-w-3xl mt-10 space-y-8 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold mb-2">1. Who runs this site</h2>
            <p className="text-muted-foreground">
              {SITE.fullName}, an independent SEO consultancy based in {SITE.city},{" "}
              {SITE.country}, operates {SITE.url} and is the data controller for personal data
              submitted through it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">2. What we collect</h2>
            <p className="text-muted-foreground">
              We only collect what you choose to send us through the contact form on{" "}
              <Link to="/contact" className="underline">/contact</Link>:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-1 text-muted-foreground">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Optional company name, budget range, and the message you write</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do not run a custom analytics pipeline on the site and we do not knowingly
              collect data from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">3. How we use it</h2>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>To reply to your enquiry and scope a possible engagement.</li>
              <li>To follow up by email about your project where it is clearly relevant.</li>
              <li>To keep a record of past conversations so we can serve returning clients.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do not sell personal data and we do not share it for advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">4. Subprocessors</h2>
            <p className="text-muted-foreground">
              The following third parties process data on our behalf so we can run the site and
              reply to you. Each has its own privacy policy.
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-1 text-muted-foreground">
              <li>
                <strong>Google</strong> — Google Workspace (email at our domain), Google Search
                Console, and Google Analytics 4 for aggregate traffic measurement.
              </li>
              <li>
                <strong>Email delivery / CRM provider</strong> — used to send and organise
                replies to your enquiries. Email {PRIVACY_CONTACT} if you need the current
                provider named in writing.
              </li>
              <li>
                <strong>Hosting / CDN</strong> — the site is served from a managed edge hosting
                platform that may process request metadata (IP, user agent) for delivery and
                abuse prevention.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">5. Retention</h2>
            <p className="text-muted-foreground">
              We keep contact form submissions and related email threads for as long as they are
              useful to the working relationship, and we delete them when you ask us to. Email
              {" "}{PRIVACY_CONTACT} and we will remove your records.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">6. Your rights</h2>
            <p className="text-muted-foreground">
              You can ask us to access, correct, export, or delete the personal data we hold
              about you, and you can object to how we use it. Send the request to{" "}
              {PRIVACY_CONTACT} and we will respond within a reasonable time, typically inside
              30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">7. Cookies</h2>
            <p className="text-muted-foreground">
              The site uses only the cookies and local storage required to operate the page
              and, where enabled, to measure aggregate traffic through Google Analytics 4. You
              can block cookies in your browser without losing access to the content.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">8. Security</h2>
            <p className="text-muted-foreground">
              We follow common-sense security practices: HTTPS everywhere, least-privilege
              access to mailboxes and CRM, and prompt removal of accounts that no longer need
              access. No online service is 100% secure; if you discover a security issue please
              email {PRIVACY_CONTACT}.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">9. Changes</h2>
            <p className="text-muted-foreground">
              We may update this policy as the site evolves. Material changes will be noted
              with a new "last updated" date at the top of this page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">10. Contact</h2>
            <p className="text-muted-foreground">
              Privacy questions and data requests:{" "}
              <a href={`mailto:${PRIVACY_CONTACT}`} className="underline">{PRIVACY_CONTACT}</a>.
              For general enquiries see{" "}
              <Link to="/contact" className="underline">/contact</Link>.
            </p>
          </section>
        </article>
      </Section>
    </>
  );
}
